import React, { useState, useEffect, useCallback, useRef } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { useTheme } from '../../contexts/ThemeContext';
import { useAuth } from '../../contexts/AuthContext';
import { Search, Heart, ChevronLeft, ChevronRight, Shuffle, ArrowLeft, X, BookOpen, BookMarked } from 'lucide-react';
import { QURAN_VERSES, VERSE_CATEGORIES, getDailyVerse } from '../../data/quranVerses';
import { CrescentStarIcon, StarOrnament, GeometricPatternBg, IslamicDivider } from '../common/IslamicIcons';
import SEO from '../common/SEO';
import SavedVersesReader from '../quran/SavedVersesReader';
import JannahGardenCard from '../quran/JannahGardenCard';
import FullQuranBrowser from '../quran/FullQuranBrowser';
import ContinueReadingCard from '../quran/ContinueReadingCard';
import { useQuranProgress } from '../../hooks/useQuranProgress';

const API_URL = process.env.REACT_APP_BACKEND_URL;
const TOKEN_KEY = 'prayer_app_token';
const LIKED_KEY = 'liked_verses_local';
const getAuthHeaders = () => {
  const t = localStorage.getItem(TOKEN_KEY);
  return t ? { 'Authorization': `Bearer ${t}` } : {};
};

const NASKH = "'Amiri', Georgia, serif";

// ── Verse Card ────────────────────────────────────────────────────────────────
const VerseCard = ({ verse, language, isDarkMode, isRTL, isLiked, onLike, animClass }) => {
  const translation = verse.translations[language] || verse.translations.en;
  return (
    <div
      className={`rounded-3xl overflow-hidden ${animClass} ${
        isDarkMode
          ? 'bg-gradient-to-br from-[var(--card-dark-from)] to-[var(--card-dark-to)] border border-white/10'
          : 'bg-gradient-to-br from-[var(--bg-light-from)] to-white border border-[var(--p-hex)]/10 shadow-xl'
      }`}
      dir={isRTL ? 'rtl' : 'ltr'}
    >
      {/* Decorative bg */}
      <GeometricPatternBg isDarkMode={isDarkMode} opacity={0.04} />
      <div className="relative z-10 p-6">
        {/* Category badge + like */}
        <div className={`flex items-center justify-between mb-5 ${isRTL ? 'flex-row-reverse' : ''}`}>
          <span className={`flex items-center gap-1.5 text-[10px] font-bold uppercase tracking-widest ${
            isDarkMode ? 'text-amber-400' : 'text-[var(--p-hex)]'
          }`}>
            <CrescentStarIcon size={12} color="currentColor" />
            {verse.category}
          </span>
          <button
            data-testid="verse-like-btn"
            onClick={onLike}
            className={`flex items-center gap-1.5 px-3 py-1.5 rounded-full text-xs font-medium transition-colors ${
              isLiked
                ? 'bg-rose-500 text-white'
                : isDarkMode ? 'bg-white/10 text-gray-400 hover:bg-white/20' : 'bg-white text-gray-500 hover:bg-gray-50 shadow-sm'
            }`}
          >
            <Heart className={`w-3.5 h-3.5 ${isLiked ? 'fill-white' : ''}`} />
            {isLiked ? 'Saved' : 'Save'}
          </button>
        </div>

        {/* Arabic text */}
        <p
          className={`text-center font-semibold mb-5 leading-loose ${isDarkMode ? 'text-amber-50' : 'text-[var(--p-hex)]'}`}
          style={{ fontFamily: NASKH, direction: 'rtl', fontSize: '1.45rem', lineHeight: '2.3' }}
        >
          {verse.arabic}
        </p>

        {/* Divider */}
        <IslamicDivider isDarkMode={isDarkMode} className="mb-5 opacity-50" />

        {/* Translation */}
        {language !== 'ar' && (
          <p className={`text-center text-base font-medium leading-relaxed mb-5 ${
            isDarkMode ? 'text-gray-100' : 'text-gray-700'
          }`}>
            {translation}
          </p>
        )}

        {/* Reference */}
        <p className={`text-center text-sm font-semibold tracking-wide ${
          isDarkMode ? 'text-amber-400/80' : 'text-[var(--p-hex)]/70'
        }`}>
          ﴾ {verse.reference} ﴿
        </p>
      </div>
    </div>
  );
};

// ── Main QuranPage ─────────────────────────────────────────────────────────────
const QuranPage = () => {
  const { t } = useTranslation();
  const { isDarkMode, language, isRTL } = useTheme();
  const { isAuthenticated } = useAuth();
  const navigate = useNavigate();

  const [search, setSearch] = useState('');
  const [category, setCategory] = useState('all');
  const [showSearch, setShowSearch] = useState(false);
  const [activeTab, setActiveTab] = useState('full'); // Default: Full Quran surah browser
  const [recitedCount, setRecitedCount] = useState(() => {
    try { return JSON.parse(localStorage.getItem('recited_verse_ids') || '[]').length; }
    catch { return 0; }
  });
  const { getMostRecent } = useQuranProgress();
  const [showContinue, setShowContinue] = useState(true);
  const mostRecent = getMostRecent();

  const [searchParams] = useSearchParams();
  // Handle ?arabic=1 from QuranBanner and ?tab= deep links
  useEffect(() => {
    if (searchParams.get('arabic') === '1') {
      localStorage.setItem('quran_arabic_only', 'true');
      setActiveTab('full');
    }
    const tab = searchParams.get('tab');
    if (tab && ['browse', 'full', 'saved'].includes(tab)) setActiveTab(tab);
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  const [likedIds, setLikedIds] = useState(() => {
    try { return new Set(JSON.parse(localStorage.getItem(LIKED_KEY) || '[]')); }
    catch { return new Set(); }
  });

  // Animation state
  const [animClass, setAnimClass] = useState('');
  const [currentIndex, setCurrentIndex] = useState(0);
  const isAnimating = useRef(false);

  // Touch/swipe
  const touchStartX = useRef(null);

  // Load liked IDs from backend
  useEffect(() => {
    if (!isAuthenticated) return;
    fetch(`${API_URL}/api/verses/liked`, { headers: getAuthHeaders() })
      .then(r => r.ok ? r.json() : null)
      .then(d => { if (d?.liked_ids) { const s = new Set(d.liked_ids); setLikedIds(s); localStorage.setItem(LIKED_KEY, JSON.stringify([...s])); } })
      .catch(() => {});
  }, [isAuthenticated]);

  // Filtered verses
  const filtered = QURAN_VERSES.filter(v => {
    const matchCat = category === 'all' || v.category === category;
    const matchSearch = !search || v.arabic.includes(search) ||
      (v.translations[language] || v.translations.en).toLowerCase().includes(search.toLowerCase()) ||
      v.reference.toLowerCase().includes(search.toLowerCase());
    return matchCat && matchSearch;
  });

  // Clamp index
  const safeIndex = Math.min(currentIndex, Math.max(0, filtered.length - 1));
  const verse = filtered[safeIndex];

  const navigate_verse = useCallback((dir) => {
    if (isAnimating.current || filtered.length <= 1) return;
    isAnimating.current = true;

    const outClass = dir === 'next' ? 'page-out-left' : 'page-out-right';
    const inClass = dir === 'next' ? 'page-in-left' : 'page-in-right';

    setAnimClass(outClass);
    setTimeout(() => {
      setCurrentIndex(prev => {
        if (dir === 'next') return (prev + 1) % filtered.length;
        return (prev - 1 + filtered.length) % filtered.length;
      });
      setAnimClass(inClass);
      setTimeout(() => { setAnimClass(''); isAnimating.current = false; }, 280);
    }, 220);
  }, [filtered.length]);

  const handleRandom = () => {
    const randIdx = Math.floor(Math.random() * filtered.length);
    setAnimClass('page-in-left');
    setCurrentIndex(randIdx);
    setTimeout(() => setAnimClass(''), 280);
  };

  // Keyboard navigation
  useEffect(() => {
    const handler = (e) => {
      if (e.key === 'ArrowRight' || e.key === 'ArrowDown') navigate_verse(isRTL ? 'prev' : 'next');
      if (e.key === 'ArrowLeft' || e.key === 'ArrowUp') navigate_verse(isRTL ? 'next' : 'prev');
    };
    window.addEventListener('keydown', handler);
    return () => window.removeEventListener('keydown', handler);
  }, [navigate_verse, isRTL]);

  // Touch swipe
  const handleTouchStart = (e) => { touchStartX.current = e.touches[0].clientX; };
  const handleTouchEnd = (e) => {
    if (touchStartX.current === null) return;
    const dx = e.changedTouches[0].clientX - touchStartX.current;
    touchStartX.current = null;
    if (Math.abs(dx) < 40) return;
    navigate_verse(dx < 0 ? (isRTL ? 'prev' : 'next') : (isRTL ? 'next' : 'prev'));
  };

  const toggleLike = async () => {
    if (!verse) return;
    const newLiked = !likedIds.has(verse.id);
    const newSet = new Set(likedIds);
    if (newLiked) newSet.add(verse.id); else newSet.delete(verse.id);
    setLikedIds(newSet);
    localStorage.setItem(LIKED_KEY, JSON.stringify([...newSet]));
    if (!isAuthenticated) return;
    try {
      await fetch(`${API_URL}/api/verses/like`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', ...getAuthHeaders() },
        body: JSON.stringify({ verse_id: verse.id, liked: newLiked }),
      });
    } catch (e) { console.error(e); }
  };

  // Category chip labels
  const getCatLabel = (cat) => {
    const c = VERSE_CATEGORIES.find(x => x.id === cat);
    return c ? (c.label[language] || c.label.en) : cat;
  };

  const bgClass = isDarkMode
    ? 'bg-gradient-to-b from-[var(--bg-dark-via)] via-[var(--bg-dark-from)] to-[var(--bg-dark-anchor)]'
    : 'bg-gradient-to-b from-[var(--bg-light-via)] via-[var(--bg-light-from)] to-white';

  return (
    <>
      <SEO title="Quran Verses" description="Browse 105 curated Quran verses with translations in 5 languages" path="/quran" />
      <div className={`min-h-screen ${bgClass}`} dir={isRTL ? 'rtl' : 'ltr'}>

        {/* ── Header ── */}
        <div className="px-4 pt-4 pb-3">
          <div className={`flex items-center justify-between ${isRTL ? 'flex-row-reverse' : ''}`}>
            <button onClick={() => navigate('/')}
              className={`flex items-center gap-1.5 text-sm font-medium transition-colors ${
                isDarkMode ? 'text-gray-400 hover:text-white' : 'text-gray-500 hover:text-gray-800'
              }`}>
              <ArrowLeft className={`w-4 h-4 ${isRTL ? 'rotate-180' : ''}`} />
            </button>
            <div className="flex items-center gap-2" />
            <div className={`flex items-center gap-2 ${isRTL ? 'flex-row-reverse' : ''}`}>
              <button onClick={handleRandom}
                className={`p-2 rounded-full transition-colors ${isDarkMode ? 'bg-white/10 text-gray-300 hover:bg-white/20' : 'bg-gray-100 text-gray-600 hover:bg-gray-200'}`}>
                <Shuffle className="w-4 h-4" />
              </button>
              <button onClick={() => setShowSearch(p => !p)}
                className={`p-2 rounded-full transition-colors ${showSearch ? 'bg-[var(--p-hex)] text-white' : isDarkMode ? 'bg-white/10 text-gray-300 hover:bg-white/20' : 'bg-gray-100 text-gray-600 hover:bg-gray-200'}`}>
                {showSearch ? <X className="w-4 h-4" /> : <Search className="w-4 h-4" />}
              </button>
            </div>
          </div>

          {/* Counter — only in browse tab */}
          {activeTab === 'browse' && (
            <p className={`text-center text-xs mt-1 ${isDarkMode ? 'text-gray-400' : 'text-gray-400'}`}>
              <span className={`font-bold ${isDarkMode ? 'text-amber-400' : 'text-[var(--p-hex)]'}`}>{safeIndex + 1}</span>
              {' / '}{filtered.length}
            </p>
          )}
        </div>

        {/* ── Continue reading card ── */}
        {showContinue && mostRecent && (
          <div className="px-4 mb-3">
            <ContinueReadingCard
              lastReadEntry={mostRecent}
              chapterId={parseInt(mostRecent[0], 10)}
              onDismiss={() => setShowContinue(false)}
            />
          </div>
        )}

        {/* ── Tab switcher ── */}
        <div className="px-4 mb-3">
          <div className={`flex gap-1 p-1 rounded-2xl ${isDarkMode ? 'bg-white/5' : 'bg-gray-100'}`}>
            {[
              { key: 'browse',   label: t('jannah.browseTab', 'Browse'),    icon: <BookOpen className="w-3.5 h-3.5" /> },
              { key: 'full',     label: language === 'ar' ? 'القرآن' : 'Full Quran',   icon: <BookMarked className="w-3.5 h-3.5" /> },
              { key: 'saved',    label: t('jannah.savedTab', 'My Saved'),   icon: <Heart className="w-3.5 h-3.5" />, count: recitedCount > 0 ? `🌱 ${recitedCount}` : undefined },
            ].map(tab => (
              <button
                key={tab.key}
                data-testid={`quran-tab-${tab.key}`}
                onClick={() => setActiveTab(tab.key)}
                className={`flex-1 flex items-center justify-center gap-1.5 py-2 rounded-xl text-xs font-semibold transition-colors ${
                  activeTab === tab.key
                    ? isDarkMode ? 'bg-amber-500/20 text-amber-400' : 'bg-white text-[var(--p-hex)] shadow-sm'
                    : isDarkMode ? 'text-gray-400 hover:text-gray-300' : 'text-gray-500 hover:text-gray-700'
                }`}
              >
                {tab.icon}
                {tab.label}
                {tab.count && <span className="text-[10px] font-bold ml-0.5">{tab.count}</span>}
              </button>
            ))}
          </div>
        </div>

        {/* ── Search (browse tab only) ── */}
        {activeTab === 'browse' && showSearch && (
          <div className="px-4 mb-3 animate-fade-up">
            <div className={`flex items-center gap-2 px-3 py-2.5 rounded-xl ${
              isDarkMode ? 'bg-white/8 border border-white/10' : 'bg-white border border-gray-200 shadow-sm'
            }`}>
              <Search className={`w-4 h-4 flex-shrink-0 ${isDarkMode ? 'text-gray-400' : 'text-gray-400'}`} />
              <input autoFocus type="text" placeholder="Search Arabic or translation..."
                value={search} onChange={e => { setSearch(e.target.value); setCurrentIndex(0); }}
                className={`flex-1 bg-transparent text-sm outline-none ${isDarkMode ? 'text-white placeholder-gray-500' : 'text-gray-800 placeholder-gray-400'}`}
              />
              {search && <button onClick={() => setSearch('')}><X className="w-3.5 h-3.5 text-gray-400" /></button>}
            </div>
          </div>
        )}

        {activeTab === 'browse' ? (<>
          {/* existing browse content */}
          {/* ── Category filter ── */}
          <div className="px-4 mb-3">
            <div className={`flex gap-2 pb-1 overflow-x-auto ${isRTL ? 'flex-row-reverse' : ''}`}
              style={{ scrollbarWidth: 'none', msOverflowStyle: 'none', WebkitOverflowScrolling: 'touch' }}>
              {VERSE_CATEGORIES.map(cat => (
                <button key={cat.id} onClick={() => { setCategory(cat.id); setCurrentIndex(0); }}
                  className={`flex-shrink-0 text-xs px-3 py-1.5 rounded-full font-medium transition-colors ${
                    category === cat.id
                      ? 'bg-[var(--p-hex)] text-white'
                      : isDarkMode ? 'bg-white/8 text-gray-400 hover:bg-white/15' : 'bg-white text-gray-500 hover:bg-gray-100 shadow-sm border border-gray-100'
                  }`}>
                  {getCatLabel(cat.id)}
                </button>
              ))}
            </div>
          </div>

          {/* ── Verse Card ── */}
          <div className="px-4 mb-4" onTouchStart={handleTouchStart} onTouchEnd={handleTouchEnd}>
            {verse ? (
              <VerseCard verse={verse} language={language} isDarkMode={isDarkMode} isRTL={isRTL}
                isLiked={likedIds.has(verse.id)} onLike={toggleLike} animClass={animClass} />
            ) : (
              <div className={`rounded-3xl p-12 text-center ${isDarkMode ? 'bg-white/5' : 'bg-white shadow-sm'}`}>
                <BookOpen className={`w-10 h-10 mx-auto mb-3 ${isDarkMode ? 'text-gray-300' : 'text-gray-300'}`} />
                <p className={`text-sm ${isDarkMode ? 'text-gray-400' : 'text-gray-400'}`}>
                  No verses found. Try a different search.
                </p>
              </div>
            )}
          </div>

          {/* ── Navigation controls ── */}
          <div className={`px-4 mb-6 flex items-center justify-between ${isRTL ? 'flex-row-reverse' : ''}`}>
            <button onClick={() => navigate_verse(isRTL ? 'next' : 'prev')} disabled={filtered.length <= 1}
              className={`flex items-center gap-2 px-5 py-2.5 rounded-2xl font-semibold text-sm transition-colors ${
                isDarkMode ? 'bg-white/10 text-white hover:bg-white/20 disabled:opacity-30' : 'bg-white text-gray-700 hover:bg-gray-50 shadow-sm border border-gray-100 disabled:opacity-30'
              }`}>
              <ChevronLeft className={`w-4 h-4 ${isRTL ? 'rotate-180' : ''}`} />
              Prev
            </button>
            <div className="flex items-center gap-1.5">
              {Array.from({ length: Math.min(filtered.length, 7) }, (_, i) => {
                const total = filtered.length;
                let dotIdx;
                if (total <= 7) dotIdx = i;
                else if (safeIndex <= 3) dotIdx = i;
                else if (safeIndex >= total - 4) dotIdx = total - 7 + i;
                else dotIdx = safeIndex - 3 + i;
                const isActive = dotIdx === safeIndex;
                return (
                  <button key={i}
                    onClick={() => { setCurrentIndex(dotIdx); setAnimClass('page-in-left'); setTimeout(() => setAnimClass(''), 280); }}
                    className={`rounded-full transition-all ${
                      isActive ? 'w-5 h-2 bg-[var(--p-hex)]' : isDarkMode ? 'w-2 h-2 bg-white/20 hover:bg-white/40' : 'w-2 h-2 bg-gray-200 hover:bg-gray-400'
                    }`}
                  />
                );
              })}
            </div>
            <button onClick={() => navigate_verse(isRTL ? 'prev' : 'next')} disabled={filtered.length <= 1}
              className={`flex items-center gap-2 px-5 py-2.5 rounded-2xl font-semibold text-sm transition-colors ${
                isDarkMode ? 'bg-[var(--p-hex)] text-white hover:bg-[var(--p-light-hex)] disabled:opacity-30' : 'bg-[var(--p-hex)] text-white hover:bg-[var(--p-light-hex)] shadow-sm disabled:opacity-30'
              }`}>
              Next
              <ChevronRight className={`w-4 h-4 ${isRTL ? 'rotate-180' : ''}`} />
            </button>
          </div>

          {/* ── Swipe hint ── */}
          <p className={`text-center text-xs pb-4 ${isDarkMode ? 'text-gray-400' : 'text-gray-400'}`}>
            Swipe left/right or use arrow keys to navigate
          </p>
        </>) : activeTab === 'full' ? (
          <div className="px-4 pb-4">
            <FullQuranBrowser />
          </div>
        ) : (<>
          {/* ── Saved tab: Garden + Reader ── */}
          <div className="px-4 space-y-4 pb-4">
            <JannahGardenCard recitedCount={recitedCount} />
            <SavedVersesReader onRecited={setRecitedCount} />
          </div>
        </>)}
      </div>
    </>
  );
};

export default QuranPage;
